import { memo, useCallback } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { SlideOut, SlideOutHeader } from '@aimsos/ui';

import { useSaveNote } from '../../hooks/useSaveNote.js';
import NoteForm from './NoteForm.jsx';

/**
 * Create a note from the Actions button (CT-5528).
 *
 * `SlideOut` supplies the overlay, the focus trap, Escape and the scroll lock —
 * none of it is rebuilt here. The form is mounted only while the panel is open,
 * so closing discards what was typed and reopening starts empty.
 *
 * Closing happens on SUCCESS only. A failed save keeps the panel open with every
 * value intact and the toast says what went wrong.
 *
 * `subtitle` lets a caller say where the note goes (History's "On <name>. It
 * joins this record's activity…", CT-5724); it defaults to the generic line.
 */
const NoteFormPanel = memo(function NoteFormPanel({ onClose, entityType, entityId, onSaved, subtitle }) {
  const { t } = useTranslation('unified-profiles');
  const { save, saving } = useSaveNote({ entityType, entityId, onSaved });

  const handleSubmit = useCallback(
    async (payload) => {
      const id = await save(payload);
      if (id) onClose();
      return id;
    },
    [save, onClose],
  );

  const renderHeader = useCallback(
    (titleId) => (
      <SlideOutHeader
        titleId={titleId}
        title={t('notes.form.createTitle')}
        subtitle={subtitle ?? t('notes.form.createSubtitle')}
        showClose
        onClose={onClose}
      />
    ),
    [t, onClose, subtitle],
  );

  return (
    <SlideOut isOpen onClose={onClose} renderHeader={renderHeader} size="M" ariaLabel={t('notes.form.createTitle')}>
      <NoteForm mode="create" saving={saving} onSubmit={handleSubmit} onCancel={onClose} />
    </SlideOut>
  );
});

/**
 * The panel — and every hook it needs, `useToast` included — exists only while it
 * is open. A page that offers *Create note* but never opens it mounts none of it,
 * so it does not need a toast provider until someone actually writes a note.
 */
const NoteFormSlideOut = memo(function NoteFormSlideOut({ isOpen, ...props }) {
  return isOpen ? <NoteFormPanel {...props} /> : null;
});

export default NoteFormSlideOut;
